import { useEffect, useState } from "react";

const MAX_CACHE_SIZE = 30;
const urlToBlobUrl = new Map<string, string>();
const urlOrder: string[] = [];

function evictOldest() {
  const oldest = urlOrder.shift();
  if (oldest) {
    const blobUrl = urlToBlobUrl.get(oldest);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    urlToBlobUrl.delete(oldest);
  }
}

function getCachedBlobUrl(url: string): string | undefined {
  return urlToBlobUrl.get(url);
}

function setCachedBlobUrl(url: string, blobUrl: string) {
  if (urlToBlobUrl.size >= MAX_CACHE_SIZE) evictOldest();
  urlToBlobUrl.set(url, blobUrl);
  urlOrder.push(url);
}

/**
 * Returns a stable image src (blob URL when cached) to avoid re-fetch flicker
 * on re-renders. Fetches the image as blob once, caches it, and reuses the
 * blob URL for the same URL.
 */
export function useImageUrl(url: string | undefined): string | undefined {
  const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(() =>
    url ? (getCachedBlobUrl(url) ?? url) : undefined
  );

  useEffect(() => {
    if (!url) {
      setResolvedSrc(undefined);
      return;
    }

    const cached = getCachedBlobUrl(url);
    if (cached) {
      setResolvedSrc(cached);
      return;
    }

    setResolvedSrc(url);
    let cancelled = false;
    fetch(url, { mode: "cors" })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error("Not ok"))))
      .then((blob) => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(blob);
        setCachedBlobUrl(url, blobUrl);
        setResolvedSrc(blobUrl);
      })
      .catch(() => {
        // Keep showing url on error
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return resolvedSrc;
}
